// The fake api's provider-priced add-ons (F-BOOT-17, spec/14 §4.5), past the HQ guard in `fake-server.ts`
// (`hq:subscriptions`):
//
//   GET   /v1/hq/addons                                          listHqAddons               the catalogue
//   GET   /v1/hq/organizations/{o}/addons                        listHqOrganizationAddons   its add-ons and the monthly total
//   POST  /v1/hq/organizations/{o}/addons                        attachHqOrganizationAddon  attach for N PCs at today's price
//   PATCH /v1/hq/organizations/{o}/addons/{organizationAddonId}  updateHqOrganizationAddon  change the PC count, or cancel
//
// Money is integer minor units, the charge `quantity × unit price` as the server computes it (`@clavian/domain`). Bodies
// are checked with the contract's own schemas.

import type { Addon, OrganizationAddon, Problem } from '@clavian/api-client';
import { AttachOrganizationAddonSchema, CreateAddonSchema, UpdateAddonSchema, UpdateOrganizationAddonSchema } from '@clavian/contracts';
import { addonMonthlyCharge, addonsMonthlyTotal } from '@clavian/domain/addons';

import type { FakeResponse } from './fake-server';

const NOW = '2026-10-14T10:00:00.000Z';
const CATALOGUE = /^\/v1\/hq\/addons(\/[^/]+)?$/;
const ORGANIZATION = /^\/v1\/hq\/organizations\/([^/]+)\/addons(\/([^/]+))?$/;

interface Helpers {
  readonly json: (status: number, body: unknown, headers?: Record<string, string>) => FakeResponse;
  readonly problem: (status: number, code: string, title: string, extra?: Partial<Problem>) => FakeResponse;
}

export function createFakeHqAddons(currencyOf: (organizationId: string) => string) {
  const state = {
    /** The catalogue: Clavian Boot at ₹150.00 per PC a month unless a test says otherwise. */
    catalogue: [
      { key: 'boot', name: 'Clavian Boot', pricing: 'per_pc', price: { amount: 15_000, currency: 'INR' }, active: true, createdAt: NOW, updatedAt: NOW },
    ] as Addon[],
    addons: new Map<string, OrganizationAddon[]>(),
    /** Every attach and change body, as sent. */
    writes: [] as unknown[],
    next: 1,
  };

  const listOf = (organizationId: string): OrganizationAddon[] => state.addons.get(organizationId) ?? [];

  const validation = (issues: readonly { path: readonly PropertyKey[]; code: string; message: string }[]) => ({
    errors: issues.map((issue) => ({ field: issue.path.map(String).join('.') || '(request)', code: issue.code, message: issue.message })),
  });

  function handle(helpers: Helpers, method: string, pathname: string, input: Record<string, unknown>, allowed: ReadonlySet<string>): FakeResponse | undefined {
    const { json, problem } = helpers;
    const catalogue = CATALOGUE.exec(pathname);
    const organization = ORGANIZATION.exec(pathname);
    if (catalogue === null && organization === null) return undefined;
    if (!allowed.has('hq:subscriptions')) return problem(403, 'forbidden', 'Not allowed');

    if (catalogue !== null) {
      const key = catalogue[1]?.slice(1);
      if (method === 'GET' && key === undefined) return json(200, { data: state.catalogue });
      if (method === 'POST' && key === undefined) {
        const parsed = CreateAddonSchema.safeParse(input);
        if (!parsed.success) return problem(400, 'validation_failed', 'Validation failed', validation(parsed.error.issues));
        state.writes.push(input);
        if (state.catalogue.some((addon) => addon.key === parsed.data.key)) return problem(409, 'conflict', 'Conflict', { detail: 'That add-on is already in the catalogue. Change it instead.' });
        const created: Addon = { ...parsed.data, active: parsed.data.active ?? true, createdAt: NOW, updatedAt: NOW } as Addon;
        state.catalogue = [...state.catalogue, created];
        return json(201, created);
      }
      if (method === 'PATCH' && key !== undefined) {
        const parsed = UpdateAddonSchema.safeParse(input);
        if (!parsed.success) return problem(400, 'validation_failed', 'Validation failed', validation(parsed.error.issues));
        state.writes.push(input);
        const current = state.catalogue.find((addon) => addon.key === key);
        if (current === undefined) return problem(404, 'not_found', 'Not found');
        const changed: Addon = { ...current, ...parsed.data, updatedAt: NOW } as Addon;
        state.catalogue = state.catalogue.map((addon) => (addon.key === key ? changed : addon));
        return json(200, changed);
      }
      return problem(405, 'method_not_allowed', 'Method not allowed');
    }

    const organizationId = (organization as RegExpExecArray)[1] as string;
    const addonId = (organization as RegExpExecArray)[3];
    const currency = currencyOf(organizationId);

    if (addonId === undefined && method === 'GET') {
      const data = [...listOf(organizationId)].sort((a, b) => (a.status === b.status ? b.startedAt.localeCompare(a.startedAt) : a.status === 'active' ? -1 : 1));
      const charges = data.filter((addon) => addon.status === 'active').map((addon) => addon.monthlyCharge);
      return json(200, { data, monthlyTotal: addonsMonthlyTotal(charges, currency as never) });
    }

    if (addonId === undefined && method === 'POST') {
      const parsed = AttachOrganizationAddonSchema.safeParse(input);
      if (!parsed.success) return problem(400, 'validation_failed', 'Validation failed', validation(parsed.error.issues));
      state.writes.push(input);
      const listed = state.catalogue.find((addon) => addon.key === parsed.data.addonKey);
      if (listed === undefined) return problem(404, 'not_found', 'Not found', { detail: 'That add-on is not in the catalogue yet. Set its price first.' });
      if (!listed.active) return problem(409, 'conflict', 'Conflict', { detail: `${listed.name} is not offered at the moment, so it cannot be attached.` });
      if (listed.price.currency !== currency) return problem(422, 'currency_mismatch', 'Currency differs from the organisation’s');
      if (listOf(organizationId).some((addon) => addon.addonKey === listed.key && addon.status === 'active')) {
        return problem(409, 'conflict', 'Conflict', { detail: 'The organisation already has this add-on. Change its PC count instead.' });
      }
      const attached: OrganizationAddon = {
        id: `oadd_01k${String(state.next++).padStart(23, '0')}`,
        organizationId,
        addonKey: listed.key,
        name: listed.name,
        pricing: listed.pricing,
        quantity: parsed.data.quantity,
        unitPrice: listed.price,
        monthlyCharge: addonMonthlyCharge(listed.price as never, parsed.data.quantity),
        status: 'active',
        startedAt: NOW,
        endedAt: null,
        updatedAt: NOW,
      };
      state.addons.set(organizationId, [...listOf(organizationId), attached]);
      return json(201, attached);
    }

    if (addonId !== undefined && method === 'PATCH') {
      const parsed = UpdateOrganizationAddonSchema.safeParse(input);
      if (!parsed.success) return problem(400, 'validation_failed', 'Validation failed', validation(parsed.error.issues));
      state.writes.push(input);
      const current = listOf(organizationId).find((addon) => addon.id === addonId);
      if (current === undefined) return problem(404, 'not_found', 'Not found');
      if (current.status !== 'active') return problem(409, 'conflict', 'Conflict', { detail: 'That add-on is cancelled. Attach it again instead.' });
      const cancelling = parsed.data.status === 'cancelled';
      if (cancelling === (parsed.data.quantity !== undefined)) return problem(400, 'validation_failed', 'Validation failed');
      const quantity = parsed.data.quantity ?? current.quantity;
      const next: OrganizationAddon = cancelling
        ? { ...current, status: 'cancelled', endedAt: NOW, updatedAt: NOW }
        : { ...current, quantity, monthlyCharge: addonMonthlyCharge(current.unitPrice as never, quantity), updatedAt: NOW };
      state.addons.set(
        organizationId,
        listOf(organizationId).map((addon) => (addon.id === addonId ? next : addon)),
      );
      return json(200, next);
    }

    return problem(405, 'method_not_allowed', 'Method not allowed');
  }

  /** What `GET /v1/organizations/{o}/addons` answers an owner (`org:billing`): the same list, read-only. */
  function forOrganization(organizationId: string): { data: OrganizationAddon[]; monthlyTotal: unknown } {
    const data = [...listOf(organizationId)].sort((a, b) => (a.status === b.status ? b.startedAt.localeCompare(a.startedAt) : a.status === 'active' ? -1 : 1));
    const charges = data.filter((addon) => addon.status === 'active').map((addon) => addon.monthlyCharge);
    return { data, monthlyTotal: addonsMonthlyTotal(charges, currencyOf(organizationId) as never) };
  }

  return { state, handle, forOrganization };
}

export type FakeHqAddons = ReturnType<typeof createFakeHqAddons>;
